import classnames from 'classnames';
import { chain, filter, find, isEmpty, isNil, map, pathSatisfies, propEq, propOr, splitAt } from 'ramda';
import { isFalsy, noop } from 'ramda-adjunct';
import React, { FC, useCallback, useMemo } from 'react';
import { WixIcons } from '..';
import { useTrackSiteFilesAnalytics } from '../../../utils/hooks/bi/useTrackSiteFilesAnalytics';
import { MediaManagerSDKFolder } from '../../../utils/media-manager-sdk';
import { isBoard } from '../../../utils/media-manager-sdk/boards';
import { isFolder } from '../../../utils/media-manager-sdk/items';
import styles from './Breadcrumbs.scss';
import { IBreadcrumbItem, IOuter, TFolderLabel } from './Breadcrumbs.types';
import { FolderLabel } from './FolderLabel';
import { GroupedFolders } from './GroupedFolders';

const TEMP_ROOT_ID = 'root';

const RightArrow: FC = () => (
  <div className={styles.separator}>
    <WixIcons.ChevronRight />
  </div>
);

export const Breadcrumbs: FC<IOuter> = ({ className, onClick, onDrop = noop, dropDisabled, folderPath }) => {
  const { trackBreadcrumbsClick } = useTrackSiteFilesAnalytics();

  const { restItems, mainItems } = useMemo<{ mainItems: IBreadcrumbItem[]; restItems: IBreadcrumbItem[] }>(() => {
    const items = map<MediaManagerSDKFolder, IBreadcrumbItem>(
      (folder) => ({
        id: isNil(folder.id) ? TEMP_ROOT_ID : folder.id,
        value: folder,
      }),
      folderPath,
    );

    const visibleItems = filter<IBreadcrumbItem>(pathSatisfies(isFalsy, ['value', 'hideFromBreadcrumbs']), items);
    const index = Math.max(visibleItems.length - 3, 0);
    const [restItems, mainItems] = splitAt(index, visibleItems);

    return {
      restItems,
      mainItems,
    };
  }, [folderPath]);

  const handleClick = useCallback(
    (item) => {
      const itemId = item.id === TEMP_ROOT_ID ? null : item.id;
      trackBreadcrumbsClick(itemId, propOr('file', 'kind', item.value));

      const folder = find<MediaManagerSDKFolder>(propEq('id', itemId), folderPath);
      if (folder) {
        onClick(folder);
      }
    },
    [onClick, folderPath],
  );

  return (
    <div className={classnames(styles.breadcrumbs, className)}>
      {!isEmpty(restItems) && <GroupedFolders items={restItems as TFolderLabel[]} onItemClick={handleClick} />}
      {!isEmpty(restItems) && <RightArrow />}

      {chain(
        (item) => [
          isFolder(item.value) || isBoard(item.value) ? (
            <FolderLabel
              key={item.id}
              item={item as TFolderLabel}
              onDrop={onDrop}
              onClick={handleClick}
              dropDisabled={dropDisabled}
              className={!isEmpty(restItems) ? styles.four : mainItems.length === 3 ? styles.three : styles.two}
            />
          ) : (
            item.value
          ),

          <RightArrow key={`${item.id}-arrow`} />,
        ],
        mainItems,
      )}
    </div>
  );
};
